import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import { Accordion } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import {
  GET_USER_LOYALTY_CAMPAIGN_PROGRESS,
  RESET_USER_LOYALTY_CAMPAIGN_PROGRESS,
  SET_USER_LOYALTY_CAMPAIGN_PROGRESS,
} from '../../../graphql';

type LoyaltyCardSummary = {
  id: string;
  name: string;
};

type LoyaltyCampaignQueryData = {
  user?: {
    id: string;
    loyaltyCampaignProgress?: {
      loyaltyPoints?: number;
      claimedLoyaltyCards?: string[];
    };
    loyaltyCampaignRewardLogs?: Array<{
      referenceId: string;
      sourceType: string;
      productId: string;
      amount: number;
      created: string;
    }>;
  };
  liveOps?: {
    id?: string;
    loyaltyCampaignCards?: LoyaltyCardSummary[];
  };
};

type MutationVariables = {
  userId: string;
  loyaltyPoints?: number;
  claimedLoyaltyCardIds?: string[];
};

const parseClaimedIds = (value: string): string[] =>
  value
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter(Boolean);

const uniqClaimedIds = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

const LoyaltyCampaignDisplay = ({ userId }: { userId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [loyaltyPoint, setLoyaltyPoint] = useState('0');
  const [claimedCardsText, setClaimedCardsText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery<LoyaltyCampaignQueryData>(GET_USER_LOYALTY_CAMPAIGN_PROGRESS, {
    variables: { userId },
    fetchPolicy: 'no-cache',
  });

  const [setProgress, { loading: setLoading }] = useMutation<
    { setUserLoyaltyCampaignProgress: { id: string } },
    MutationVariables
  >(SET_USER_LOYALTY_CAMPAIGN_PROGRESS);
  const [resetProgress, { loading: resetLoading }] = useMutation<
    { resetUserLoyaltyCampaignProgress: { id: string } },
    MutationVariables
  >(RESET_USER_LOYALTY_CAMPAIGN_PROGRESS);

  useEffect(() => {
    const currentPoints = data?.user?.loyaltyCampaignProgress?.loyaltyPoints ?? 0;
    const currentClaimed = data?.user?.loyaltyCampaignProgress?.claimedLoyaltyCards ?? [];
    setLoyaltyPoint(String(currentPoints));
    setClaimedCardsText(currentClaimed.join(', '));
  }, [data]);

  const currentPoints = data?.user?.loyaltyCampaignProgress?.loyaltyPoints ?? 0;
  const currentClaimedCards = useMemo(
    () => data?.user?.loyaltyCampaignProgress?.claimedLoyaltyCards ?? [],
    [data?.user?.loyaltyCampaignProgress?.claimedLoyaltyCards]
  );
  const loyaltyCards = useMemo(() => data?.liveOps?.loyaltyCampaignCards ?? [], [data?.liveOps?.loyaltyCampaignCards]);
  const rewardLogs = useMemo(
    () => data?.user?.loyaltyCampaignRewardLogs ?? [],
    [data?.user?.loyaltyCampaignRewardLogs]
  );
  const parsedClaimedIds = useMemo(() => uniqClaimedIds(parseClaimedIds(claimedCardsText)), [claimedCardsText]);
  const currentClaimedIds = useMemo(() => uniqClaimedIds(currentClaimedCards), [currentClaimedCards]);
  const busy = loading || setLoading || resetLoading;

  const onSave = async () => {
    setError(null);
    setSuccess(null);

    const parsedPoints = Number(loyaltyPoint);
    if (!Number.isFinite(parsedPoints) || parsedPoints < 0) {
      setError('Loyalty Points must be a number greater than or equal to 0.');
      return;
    }

    try {
      await setProgress({
        variables: {
          userId,
          loyaltyPoints: Math.floor(parsedPoints),
          claimedLoyaltyCardIds: parsedClaimedIds,
        },
      });
      await refetch();
      setSuccess('Loyalty Campaign progress updated successfully.');
    } catch (e) {
      setError(`Failed to update Loyalty Campaign progress: ${String(e)}`);
    }
  };

  const onReset = async () => {
    setError(null);
    setSuccess(null);

    try {
      await resetProgress({ variables: { userId } });
      await refetch();
      setSuccess('Loyalty Campaign progress reset successfully.');
    } catch (e) {
      setError(`Failed to reset Loyalty Campaign progress: ${String(e)}`);
    }
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Loyalty Point
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Row className="g-2 mb-2">
                <Col md={4}>
                  <Form.Label>Loyalty Point</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={loyaltyPoint}
                    onChange={(e) => setLoyaltyPoint(e.currentTarget.value)}
                  />
                </Col>
                <Col md={8}>
                  <Form.Label>Claimed Card IDs (comma/space/newline separated)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={claimedCardsText}
                    onChange={(e) => setClaimedCardsText(e.currentTarget.value)}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-3 gap-2">
                <Button
                  variant="outline-info"
                  disabled={busy}
                  onClick={async () => {
                    await refetch();
                  }}
                >
                  Refresh
                </Button>
                <Button variant="primary" disabled={busy} onClick={onSave}>
                  Save
                </Button>
                <Button variant="outline-danger" disabled={busy} onClick={onReset}>
                  Reset
                </Button>
              </div>

              <hr />

              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Reward Logs</Form.Label>
                <small className="text-muted">{rewardLogs.length} rows</small>
              </div>
              {rewardLogs.length === 0 ? (
                <div className="text-muted small">No loyalty reward logs found for this user.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Created</th>
                        <th>Source</th>
                        <th>Amount</th>
                        <th>Product</th>
                        <th>Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rewardLogs.map((log) => (
                        <tr key={`${log.referenceId}-${log.created}`}>
                          <td>{new Date(log.created).toLocaleString()}</td>
                          <td>{log.sourceType}</td>
                          <td>{log.amount}</td>
                          <td style={{ wordBreak: 'break-all' }}>{log.productId}</td>
                          <td style={{ wordBreak: 'break-all' }}>{log.referenceId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default LoyaltyCampaignDisplay;
